import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { useContext, useCallback } from 'react'
import { useMatch, useParams } from 'react-router'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { AuthContext } from '@/context/auth-context'
import { Table } from '@/assets/icons'
import { isActiveMatterMember } from '@/matter/utils/matter-permissions'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { MattersActions } from '@/matter/store/slice'
import { selectMatterById } from '@/matter/store/selectors'
import { matterPaths, routePatterns, kRouteSegments } from '@/routing/paths'
import { MatterType } from '@/matter/store/schemas'
import { ArrowLeft, BackToHome, DraftSparkle, Folder, MatterBriefcase } from '@/assets/icons'
import { canViewChats, canViewMedChron, canViewMedicalBills, canViewTimeline } from '@/matter/utils/matter-permissions'
import { isDraftsDestinationEnabled } from '@/drafting/destination/drafts-destination-flag'
import type { SideNavItemConfig, SideNavMatterRecordsConfig, SideNavChatHistoryConfig } from '@/components/side-nav/SideNav'

// =============================================================================
// Types
// =============================================================================

type SideNavConfigDashboard = {
  variant: 'dashboard'
  items: SideNavItemConfig[]
  activeItemId: string | undefined
}

type SideNavConfigMatter = {
  variant: 'matter'
  topItems: SideNavItemConfig[]
  matterRecords?: SideNavMatterRecordsConfig
  chatHistory?: SideNavChatHistoryConfig
  activeItemId: string | undefined
}

export type SideNavConfig = SideNavConfigDashboard | SideNavConfigMatter

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook that builds navigation configuration for the SideNav component.
 *
 * Determines which variant to use based on matter context:
 * - 'dashboard': When no specific matter is selected (activeMatterId is null/"all")
 * - 'matter': When a specific matter is selected (even on non-matter routes like settings)
 *
 * This allows users to maintain their navigation context when visiting settings,
 * account, or other non-matter routes while working within a matter.
 *
 * Handles:
 * - Active item detection via route matching
 * - Anonymous user lock icons
 * - Matter Records section: thin config `{ matterId }` — the `MatterRecords` component
 *   owns asset selection, item building, and modal state internally.
 */
export function useSideNavConfig(): SideNavConfig {
  const { enabled: tablesEnabled, loading: tablesLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.TABLES)
  const dispatch = useAppDispatch()
  const authContext = useContext(AuthContext)

  // User state
  const userIsAnon = authContext?.user?.isAnonymous ?? false

  // Matter state - determines sidebar variant
  const activeMatterId = useAppSelector(selectActiveMatterId)
  const activeMatter = useAppSelector((state) => selectMatterById(state, { matterId: activeMatterId }))
  const personalWorkspace = useAppSelector((state) => state.mattersState.personalWorkspace)

  // Check if user has an active matter context
  // This drives sidebar variant, independent of current route
  const hasMatterContext = activeMatterId !== null

  // The open chat, for the Chat History section's active-row highlighting — ChatHistory fetches
  // and owns its own list/loading state, so this is the only chat-history-specific data
  // useSideNavConfig needs.
  const { chatId } = useParams()

  // Handler to clear matter context when Home is clicked
  const handleHomeClick = useCallback(() => {
    dispatch(MattersActions.setActiveMatterId({ matterId: null }))
  }, [dispatch])

  // Route matching for active states
  const isMattersRoute = useMatch('/dashboard/matters')
  const isTablesRoute = useMatch('/dashboard/m/:matterId/tables/*')
  const isChronologyRoute = useMatch(routePatterns.chronology.matterFirst)
  const isTimelineRoute = useMatch(routePatterns.timeline.matterFirst)
  const isBillingRoute = useMatch(routePatterns.billingSummaries.matterFirst)
  const isUserFilesRoute = useMatch(routePatterns.userFiles.matterFirst)
  const isMatterOverviewRoute = useMatch({ path: routePatterns.matterOverview, end: true })
  // `end: true`, unlike the feature routes above: Drafts is selected on the list and nowhere else.
  // Opening a draft navigates to its source chat, where Chat History takes over the selection.
  const isDraftsRoute = useMatch({ path: routePatterns.drafts, end: true })

  // =============================================================================
  // Dashboard Variant (no specific matter selected)
  // =============================================================================

  if (!hasMatterContext) {
    const items: SideNavItemConfig[] = [
      {
        id: 'matters',
        label: 'Matters',
        href: '/dashboard/matters',
        icon: MatterBriefcase,
        showLock: userIsAnon,
      },
    ]
    if (personalWorkspace && personalWorkspace.user_id === authContext?.user?.uid)
      items.push({
        id: 'outlook-chats',
        label: 'Personal Outlook chats',
        href: matterPaths.chat.feature(personalWorkspace.id, kRouteSegments.agentV2),
        icon: DraftSparkle,
      })

    // Determine active item
    let activeItemId: string | undefined
    if (isMattersRoute) activeItemId = 'matters'

    return {
      variant: 'dashboard',
      items,
      activeItemId,
    }
  }

  // =============================================================================
  // Matter Variant (specific matter selected)
  // =============================================================================

  if (activeMatter?.type === MatterType.OUTLOOK_WORKSPACE)
    return {
      variant: 'matter',
      topItems: [{ id: 'home', label: 'Back to all matters', href: '/dashboard/matters', icon: ArrowLeft, onClick: handleHomeClick }],
      chatHistory: { matterId: activeMatter.id, activeChatId: chatId, allowNewChat: false },
      activeItemId: undefined,
    }

  // Home renders as the muted "Back to all matters" link (Figma: Global Updates, node 2466-82125)
  const topItems: SideNavItemConfig[] = [
    {
      id: 'home',
      label: 'Back to all matters',
      href: '/dashboard/matters',
      icon: ArrowLeft,
      railIcon: BackToHome,
      iconClassName: 'p-[2px]',
      variant: 'subtle',
      onClick: handleHomeClick,
    },
    {
      id: 'matter-overview',
      label: 'Matter Overview',
      href: matterPaths.overview(activeMatterId),
      icon: MatterBriefcase,
      showLock: userIsAnon,
    },
  ]

  topItems.push({ id: 'user-files', label: 'Matter Files', href: matterPaths.userFiles.root(activeMatterId), icon: Folder, showLock: userIsAnon })

  // Drafts sits below Matter Files, per the destination design. Same accepted-member rule as Chat
  // History and the destination's own route guard, because a draft comes from the matter's
  // conversations — and hiding the item is presentation, not authorization; the route guard and the
  // backend still decide who gets the list.
  if (isDraftsDestinationEnabled() && canViewChats(activeMatter)) {
    topItems.push({ id: 'drafts', label: 'Drafts', href: matterPaths.drafts(activeMatterId), icon: DraftSparkle, showLock: userIsAnon })
  }
  if (!tablesLoading && tablesEnabled && isActiveMatterMember(activeMatter)) {
    topItems.push({ id: 'tables', label: 'Tables', href: matterPaths.tables(activeMatterId), icon: Table, showBetaTag: true })
  }

  // Surface the Matter Records section whenever the viewer can see at least one record type.
  // The MatterRecords component owns asset selection, item building, and modal state internally.
  const canViewAnyRecord = canViewTimeline(activeMatter) || canViewMedChron(activeMatter) || canViewMedicalBills(activeMatter)
  const matterRecords: SideNavMatterRecordsConfig | undefined = canViewAnyRecord ? { matterId: activeMatterId } : undefined

  // Determine active item
  let activeItemId: string | undefined
  if (isMatterOverviewRoute) activeItemId = 'matter-overview'
  else if (isTablesRoute) activeItemId = 'tables'
  else if (isDraftsRoute) activeItemId = 'drafts'
  else if (isUserFilesRoute) activeItemId = 'user-files'
  else if (isChronologyRoute) activeItemId = 'chronology'
  else if (isTimelineRoute) activeItemId = 'timeline'
  else if (isBillingRoute) activeItemId = 'billing-summary'

  const chatHistory: SideNavChatHistoryConfig | undefined = canViewChats(activeMatter) ? { matterId: activeMatterId, activeChatId: chatId } : undefined

  return {
    variant: 'matter',
    topItems,
    matterRecords,
    chatHistory,
    activeItemId,
  }
}
